import React, { Fragment, ReactElement, ReactNode, RefObject } from "react";
import {
  Cell,
  Column,
  Row,
  TableBodyPropGetter,
  TableBodyProps,
  TableCellProps,
  TableRowProps,
} from "react-table";
import { ColumnWithClassname } from "@src/components/modules/Table/tableInterfaces";

interface Props<T extends Record<string, unknown>> {
  getTableBodyProps: (
    propGetter?: TableBodyPropGetter<T> | undefined,
  ) => TableBodyProps;
  page: Row<T>[];
  prepareRow: (row: Row<T>) => void;
  getRowProps?: (row: Row<T>) => Partial<TableRowProps>;
  getColumnProps: (column: Column<T>) => Partial<TableCellProps>;
  getCellProps: (cell: Cell<T>) => Partial<TableCellProps>;
  renderRowSubComponent: (
    row: Row<T>,
    ref: RefObject<HTMLDivElement>,
  ) => ReactNode;
  tableWrapRef: RefObject<HTMLDivElement>;
}

export const PaginatedTableBody = <T extends Record<string, unknown>>(
  props: Props<T>,
): ReactElement => {
  const {
    getTableBodyProps,
    page,
    prepareRow,
    getRowProps,
    getColumnProps,
    getCellProps,
    renderRowSubComponent,
    tableWrapRef,
  } = props;
  return (
    <div className="tbody" {...getTableBodyProps()}>
      {page.map((row) => {
        prepareRow(row);
        let rowProps;
        if (getRowProps) {
          rowProps = row.getRowProps(getRowProps(row));
        } else {
          rowProps = row.getRowProps();
        }
        return (
          <Fragment key={rowProps.key}>
            <div {...rowProps} className={`${rowProps.className || ""} tr`}>
              {row.cells.map((cell) => {
                const { key, ...restCellProps } = cell.getCellProps([
                  {
                    className: (
                      cell.column as ColumnWithClassname<
                        Record<string, unknown>
                      >
                    ).className,
                    style: (
                      cell.column as ColumnWithClassname<
                        Record<string, unknown>
                      >
                    ).style,
                  },
                  getColumnProps(cell.column),
                  getCellProps(cell),
                ]);
                return (
                  cell.column as ColumnWithClassname<Record<string, unknown>>
                ).isSticky ? (
                  <div
                    {...restCellProps}
                    className={`${
                      restCellProps.className || ""
                    } th overflow-width-ellipsis`}
                    key={key}
                  >
                    {cell.render("Cell")}
                  </div>
                ) : (
                  <div
                    {...restCellProps}
                    className={`${
                      restCellProps.className || ""
                    } td overflow-width-ellipsis row`}
                    key={key}
                  >
                    {cell.render("Cell")}
                  </div>
                );
              })}
            </div>
            {row.isExpanded ? (
              <div className="tr">
                <div className="td sub-table">
                  {renderRowSubComponent(row, tableWrapRef)}
                </div>
              </div>
            ) : null}
          </Fragment>
        );
      })}
    </div>
  );
};
